import { FILE_JOB_MAX_FILES_PER_REQUEST } from '@atlas/types'
import { AssetListSelectionBar, AssetListSelectionStatus } from '@atlas/ui-domain'
import { Button, Checkbox } from '@atlas/ui-primitives'
import { FileArchive } from 'lucide-react'
import { useCan } from '@/lib/capabilities'
import { REFUSAL, Restricted } from '@/lib/refusal'

/**
 * The ZIP as this page's own words for the shared bulk action, which otherwise reads as a
 * plain download.
 *
 * The way IN carries the outcome, because at rest that button is the only thing the page
 * shows of the feature: a reader who is told only to "select assets" has no reason to think
 * selecting leads to an archive.
 */
const ZIP_ACTION = {
	enterLabel: 'Select files for a ZIP',
	label: (count: number) => (count ? `Download ZIP · ${count} ${count === 1 ? 'file' : 'files'}` : 'Download ZIP'),
	pendingLabel: 'Requesting…',
	icon: FileArchive,
}

/** Restates the outcome where the picking happens, so the grid is not one mode with no stated purpose. */
const SELECTING_HINT = 'Pick the files to package into one ZIP.'

const OVER_CAP_MESSAGE = `Too many files — a ZIP takes at most ${FILE_JOB_MAX_FILES_PER_REQUEST}. Deselect some to continue.`

/**
 * Everything the header needs to run the ZIP selection, or absent where there is
 * nothing to select — an empty grid that nobody is already picking from.
 */
export interface LibrarySelectionActions {
	/** Whether the grid is selecting: its cards are toggles, and this header offers the actions. */
	isSelecting: boolean
	/** The COMPLETE selection count, across every page visited — not just what is on screen. */
	selectedCount: number
	/** Every File on the visible page is selected. */
	allOnPageSelected: boolean
	/** Some, but not all, Files on the visible page are selected. */
	someOnPageSelected: boolean
	/** The selection is larger than one ZIP request may carry, so the action is refused. */
	overCap: boolean
	start: () => void
	/** Leave the mode and drop the selection — the two are one action, never two. */
	exit: () => void
	/** Select the remainder of the visible page, or deselect exactly that page when it is already full. */
	togglePage: () => void
	downloadZip: () => void
}

interface LibraryHeaderProps {
	/** Files on the CURRENT page — discovery reports no total, so nothing here claims one. */
	fileCount: number
	onUpload: () => void
	selection?: LibrarySelectionActions
}

/**
 * Library's title block, its one entry point into upload & classify, and the ZIP
 * selection's whole action surface.
 *
 * Selecting is a MODE rather than a permanent bar over the grid, on the same
 * shape as the work-order asset lists: at rest the header offers one way in, and
 * only inside the mode does it carry what can be done with a selection. That is
 * what keeps a page nobody is packaging from spending a row on it.
 *
 * `Select all` is deliberately page-scoped — it never reaches the unloaded
 * remainder of the current filter — so its tri-state reflects the visible page
 * while the action's count reflects the whole selection. Those two disagreeing is
 * the normal case once the user has paged, not an inconsistency.
 */
export function LibraryHeader({ fileCount, onUpload, selection }: LibraryHeaderProps) {
	const isSelecting = selection?.isSelecting ?? false
	const { canWork } = useCan()

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">Library · Asset browser</p>
					<h1 className="text-3xl font-bold text-ds-text">All files</h1>
				</div>
				<div className="flex items-center gap-2">
					{selection ? (
						<AssetListSelectionBar
							isSelecting={selection.isSelecting}
							selectionCount={selection.selectedCount}
							isDownloadPending={false}
							isDisabled={selection.overCap}
							// One row, one height: these controls stand beside `Upload & classify`.
							size="md"
							onStartSelecting={selection.start}
							onExitSelecting={selection.exit}
							onDownloadSelected={selection.downloadZip}
							action={ZIP_ACTION}
						/>
					) : null}
					{/* Withheld while selecting: in that mode every control in this row acts on the
					    selection, and an upload would be the one that means something else. */}
					{isSelecting ? null : (
						<Restricted allowed={canWork} why={REFUSAL.upload}>
							<Button className="bg-ds-text text-ds-primary-foreground hover:bg-ds-text" onClick={onUpload}>
								Upload &amp; classify
							</Button>
						</Restricted>
					)}
				</div>
			</div>
			{/* One line above the grid, whatever the mode: what is listed, or — while selecting —
			    what to do with it and how to take the whole page in one go. Entering the mode
			    swaps the line's contents rather than stacking a second and a third row under it. */}
			<div className="flex min-h-6 items-center justify-between gap-3">
				{isSelecting ? (
					<AssetListSelectionStatus
						isSelecting
						hint={SELECTING_HINT}
						outcome={selection?.overCap ? OVER_CAP_MESSAGE : null}
						isOutcomeFailure={selection?.overCap ?? false}
					/>
				) : (
					<p className="text-sm text-ds-text-muted">
						{fileCount} {fileCount === 1 ? 'file' : 'files'}
					</p>
				)}
				{selection && isSelecting ? (
					<Checkbox
						size="sm"
						aria-label="Select all files on this page"
						isSelected={selection.allOnPageSelected}
						isIndeterminate={!selection.allOnPageSelected && selection.someOnPageSelected}
						onChange={selection.togglePage}
					>
						Select all on this page
					</Checkbox>
				) : null}
			</div>
		</div>
	)
}
